// Utilitários da tela do Cakto Analytics. O backend devolve números crus; formato e régua de
// período ficam aqui para que os cards não repitam a conta cada um do seu jeito.

const TIME_ZONE = 'America/Sao_Paulo';

// Azul do Pro, como o verde da tela do WhatsApp: identifica o produto sem depender do tema. Fica
// aqui, e não na página do Pro, para outras telas usarem a cor sem carregar a página junto.
export const PRO_BLUE = '#2563EB';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const integer = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const decimal1 = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

export const fmtMoney = (value) => brl.format(Number(value) || 0);
export const fmtInt = (value) => integer.format(Number(value) || 0);
export const fmtPct = (value) => `${decimal1.format(Number(value) || 0)}%`;

/** Porcentagem segura: divisor zero vira 0 em vez de `NaN%` na tela. */
export const pct = (part, total) => (total ? (Number(part) / Number(total)) * 100 : 0);

/**
 * Pedidos que chegaram a ser aprovados no período. O `paidCount` do backend conta só o status
 * `paid` atual: o pedido reembolsado ou em disputa sai dele e vem à parte. Dividir o reembolso
 * pelo `paidCount` inflaria a taxa (tudo reembolsado daria divisão por zero ou mais de 100%).
 */
export const approvedOrders = (totals = {}) =>
  (Number(totals.paidCount) || 0) +
  (Number(totals.refundCount) || 0) +
  (Number(totals.chargebackCount) || 0) +
  (Number(totals.medCount) || 0);

/**
 * Conversão limitada a 100%. Com a coleta de visualizações começando no meio do período, entram
 * vendas de visitas que não foram contadas e a razão passa de 100%; o número cru não significa
 * nada, então a tela mostra o teto e avisa (`capped`).
 */
export const capPct = (value) => {
  const number = Number(value) || 0;
  return { value: Math.min(number, 100), capped: number > 100 };
};

/**
 * Variação contra o período anterior. `null` quando não há base (anterior zero): mostrar
 * "+∞%" ou "+100%" sugeriria uma comparação que não existe.
 */
export const delta = (current, previous) => {
  const prev = Number(previous) || 0;
  if (!prev) return null;
  return ((Number(current) - prev) / prev) * 100;
};

/** Dia de hoje em Brasília (`YYYY-MM-DD`): o backend agrupa por dia BRT, não pelo fuso do navegador. */
export const todayBrt = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE }).format(now);

export const addDays = (isoDate, days) => {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
};

export const toIso = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

export const fromIso = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** `2026-10-25` → `25/10`. */
export const fmtDayMonth = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '');

export const fmtDayMonthYear = (iso) => (iso ? `${fmtDayMonth(iso)}/${iso.slice(0, 4)}` : '');

export const fmtTime = (isoDateTime) => {
  if (!isoDateTime) return '';
  const date = new Date(isoDateTime);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

export const PERIOD_PRESETS = [
  { value: 'today', label: 'Hoje', days: 1 },
  { value: '7d', label: '7 dias', days: 7 },
  { value: '30d', label: '30 dias', days: 30 },
  { value: '90d', label: '90 dias', days: 90 },
  { value: '6m', label: '6 meses', days: 180, pro: true },
  { value: '12m', label: '12 meses', days: 365, pro: true },
  { value: 'custom', label: 'Personalizado' },
];

/** Os atalhos acima de 90 dias só aparecem com o Pro: sem ele, o backend recusa o período. */
export const periodPresets = (pro) => PERIOD_PRESETS.filter((item) => pro || !item.pro);

/** Intervalo fechado que termina hoje: "7 dias" = hoje e os 6 anteriores, como no Dashboard. */
export const presetRange = (preset, today = todayBrt()) => {
  const found = PERIOD_PRESETS.find((item) => item.value === preset);
  const days = found?.days || 30;
  return { startDate: addDays(today, -(days - 1)), endDate: today };
};

/** O backend limita a 90 dias; a tela barra antes para não mostrar erro de um pedido evitável. */
export const MAX_RANGE_DAYS = 90;
/** Com o Pro, 12 meses (366 para caber o ano bissexto). */
export const PRO_MAX_RANGE_DAYS = 366;

export const maxRangeDays = (pro) => (pro ? PRO_MAX_RANGE_DAYS : MAX_RANGE_DAYS);

/** Acima disso o gráfico agrupa por mês: 365 barras de um pixel não se leem no celular. */
export const MONTHLY_AFTER_DAYS = 92;

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

/** `2026-10` → `out/26`. */
export const fmtMonth = (isoMonth) =>
  isoMonth ? `${MONTHS[Number(isoMonth.slice(5, 7)) - 1]}/${isoMonth.slice(2, 4)}` : '';

/**
 * A série diária somada por mês quando o período passa de `MONTHLY_AFTER_DAYS`. A conversão do
 * mês sai das somas (vendas ÷ visualizações): a média das taxas diárias pesaria igual o dia de 3
 * visitas e o de 3 mil. `date` vira `YYYY-MM` e `monthly` diz ao gráfico como rotular.
 */
export const groupDaily = (daily = []) => {
  if (daily.length <= MONTHLY_AFTER_DAYS) return { monthly: false, points: daily };
  const months = new Map();
  daily.forEach((day) => {
    const key = day.date.slice(0, 7);
    const acc = months.get(key) || { date: key, checkoutViews: 0, paidCount: 0, grossAmount: 0 };
    acc.checkoutViews += Number(day.checkoutViews) || 0;
    acc.paidCount += Number(day.paidCount) || 0;
    acc.grossAmount += Number(day.grossAmount) || 0;
    months.set(key, acc);
  });
  const points = [...months.values()].map((month) => ({
    ...month,
    conversionRate: pct(month.paidCount, month.checkoutViews),
  }));
  return { monthly: true, points };
};

export const rangeDays = (startDate, endDate) =>
  Math.round((fromIso(endDate) - fromIso(startDate)) / 86400000) + 1;

/**
 * Legenda dos números que dependem da coleta no checkout (visualizações, preenchimento, origem
 * da visita). Sem ela, um período que começa antes da coleta mostraria conversão inflada sem aviso.
 */
export const trackingCaption = (trackingSince, startDate) => {
  if (!trackingSince) return 'Visualizações ainda não estão sendo contadas';
  if (startDate && trackingSince > startDate)
    return `Visualizações contadas a partir de ${fmtDayMonth(trackingSince)}`;
  return null;
};

/**
 * Início da contagem de acessos quando ela começou depois do início do período, ou `null`. Os
 * blocos que põem acessos ao lado de vendas dizem desde quando contam: as vendas são do período
 * inteiro, e sem o aviso parecia que só uma fração dos compradores tinha passado pelo checkout.
 */
export const visitsSinceInPeriod = (trackingSince, startDate) =>
  trackingSince && startDate && trackingSince > startDate ? trackingSince : null;

/**
 * A coleta de visualizações começou dentro do período escolhido, então parte das visitas ficou
 * de fora da contagem. Só aí a conversão acima de 100% tem como motivo "vendas de visitas antes
 * do início da contagem". Inclui o próprio dia de início: a coleta raramente começa à meia-noite,
 * e as visitas daquela manhã não foram contadas.
 */
export const trackingPartial = (trackingSince, period = {}) => {
  if (!trackingSince || !period.startDate) return false;
  if (period.endDate && trackingSince > period.endDate) return false;
  return trackingSince >= period.startDate;
};

/**
 * A comparação de visualizações e conversão com o período anterior só vale se a coleta cobria
 * os dois períodos inteiros. Olha o início do anterior (e não só o do atual): coleta que começou
 * no meio do anterior deixa a base baixa e o delta subiria sem ninguém ter vendido mais. O dia de
 * início não conta como coberto (`<`, não `<=`): ele costuma ser parcial.
 */
export const trackingComparable = (trackingSince, period = {}) => {
  if (!trackingSince) return false;
  const since = period.previousStartDate || period.startDate;
  return !since || trackingSince < since;
};

const DEVICE_LABELS = {
  mobile: 'Celular',
  desktop: 'Computador',
  tablet: 'Tablet',
  desconhecido: 'Não identificado',
};
export const deviceLabel = (value) => DEVICE_LABELS[value] || value;

const METHOD_LABELS = {
  pix: 'Pix',
  credit_card: 'Cartão de crédito',
  boleto: 'Boleto',
  picpay: 'PicPay',
  applepay: 'Apple Pay',
  googlepay: 'Google Pay',
  nupay: 'NuPay',
  pix_auto: 'Pix Automático',
};
export const methodLabel = (value) => METHOD_LABELS[value] || value;

const OFFER_KIND = {
  orderbump: { label: 'Order bump', color: 'primary' },
  upsell: { label: 'Upsell', color: 'info' },
  downsell: { label: 'Downsell', color: 'default' },
};
export const offerKind = (kind) => OFFER_KIND[kind] || { label: kind, color: 'default' };

let regionNames;
// O backend grava `(sem valor)` quando o pedido não trouxe o campo: venda sem UTM no link ou sem
// país identificável.
export const NO_VALUE = '(sem valor)';
// O mesmo vazio do lado da coleta do checkout (acesso sem UTM no link).
export const NO_SOURCE = '(sem origem)';

export const countryLabel = (code) => {
  if (code === NO_VALUE) return 'Não informado';
  if (!code || code.length !== 2) return code;
  try {
    regionNames = regionNames || new Intl.DisplayNames(['pt-BR'], { type: 'region' });
    return regionNames.of(code.toUpperCase()) || code;
  } catch (error) {
    // Navegador sem Intl.DisplayNames: o código ISO ainda identifica o país.
    return code;
  }
};
